import type { WebSockets } from '@pushflow/types'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userInfoAtom } from '../store/atoms'
import type { EPollType } from '../types/enums'
import { EModalName } from '../types/enums'
import { trpc } from '../utils'
import { useModal } from './useModal'

type PollStateType = WebSockets.PollTableWebSocketOutputType['data']

export const usePoll = (props: { userId: string }) => {
  const { mutateAsync: sendVote } = trpc.poll.vote.useMutation()
  const { t } = useTranslation()
  const { openConfirmationModal, closeModal } = useModal()

  const [openedPollIdByType, setOpenedPollIdByType] = useState<
    Partial<Record<EPollType, string>>
  >({})
  const [pollStateByPollId, setPollStateByPollId] = useState<
    Record<string, { voted: boolean; data: PollStateType }>
  >({})

  const clearPoll = (pollId: string) => {
    setPollStateByPollId((prev) => {
      const { [pollId]: _, ...rest } = prev
      return rest
    })
  }

  const closePreviousModal = (type: EPollType) => {
    const openedModalId = openedPollIdByType[type]
    if (openedModalId) {
      closeModal({ id: openedModalId })
    }
  }

  const openVoteModal = async (poll: PollStateType) => {
    const timeoutSeconds = Math.floor(
      (new Date(poll.expiredAt).getTime() - Date.now()) / 1000,
    )
    const { closeModal: waitForResult, id } = openConfirmationModal(
      {
        text: t('modal.confirmation.doubleBoard.text', {
          name: poll.names[poll.authorId] || '—',
        }),
        mainButtonText: t('modal.confirmation.doubleBoard.confirm'),
        secondaryButtonText: t('modal.confirmation.doubleBoard.cancel'),
        timeoutSeconds,
      },
      {
        title: t('modal.confirmation.doubleBoard.title'),
        name: EModalName.POLL_DOUBLE_BOARD,
      },
    )
    setOpenedPollIdByType((prev) => ({ ...prev, [poll.type]: id }))
    const result = await waitForResult
    return result === 'main'
  }

  const processPollUpdate = async (poll: PollStateType) => {
    if (poll.result !== null) {
      clearPoll(poll.id)
      return
    }

    const hasVoted =
      pollStateByPollId[poll.id]?.voted === true &&
      poll.votes.find(([userId, vote]) => userId === props.userId)
    if (!hasVoted) {
      closePreviousModal(poll.type)
      const vote = await openVoteModal(poll)
      await sendVote({ tableId: poll.tableId, pollId: poll.id, vote })

      setOpenedPollIdByType((prev) => {
        const { [poll.type]: _, ...rest } = prev
        return rest
      })
    }

    setPollStateByPollId((prev) => ({
      ...prev,
      [poll.id]: {
        voted: true,
        data: poll,
      },
    }))
  }

  return {
    handlePollState: processPollUpdate,
  }
}
